<template>
  <v-slide-group
    :model-value="modelValue"
    mandatory
    :show-arrows="scrollable"
    :center-active="centerActive"
    class="tab-toggle"
    :class="{
      'tab-toggle--disabled': disabled,
      'tab-toggle--scrollable': scrollable,
    }"
    :style="{'--tab-toggle-active-color': activeColorValue}"
    :disabled="disabled"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-slide-group-item
      v-for="option in options"
      :key="option.value"
      v-slot="{ isSelected, toggle }"
      :value="option.value"
    >
      <button
        type="button"
        class="tab-toggle__btn"
        :class="[
          {'tab-toggle__btn--active': isSelected},
          {'tab-toggle__btn--icon-only': option.icon && !option.label},
          {'tab-toggle__btn--wrap': wrapLabels},
        ]"
        :disabled="disabled"
        :aria-pressed="isSelected"
        :aria-label="option.label || option.value"
        @click="toggle"
      >
        <v-icon
          v-if="option.icon"
          :icon="option.icon"
          size="small"
        />
        <span v-if="option.label">{{ option.label }}</span>
      </button>
    </v-slide-group-item>
  </v-slide-group>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue';

export interface TabToggleOption {
  value: string;
  label?: string;
  icon?: string;
}

export default defineComponent({
  name: 'TabToggle',
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    options: {
      type: Array as PropType<TabToggleOption[]>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    centerActive: {
      type: Boolean,
      default: false,
    },
    wrapLabels: {
      type: Boolean,
      default: false,
    },
    activeColor: {
      type: String,
      default: 'secondary',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      indicatorEl: null as HTMLElement | null,
      scrollParent: null as HTMLElement | null,
      resizeObserver: null as ResizeObserver | null,
      lastLeft: 0,
      lastWidth: 0,
      lastHeight: 0,
    };
  },
  computed: {
    activeColorValue(): string {
      if (
        this.activeColor.startsWith('#') ||
        this.activeColor.startsWith('rgb') ||
        this.activeColor.startsWith('var(') ||
        this.activeColor.startsWith('hsl')
      ) {
        return this.activeColor;
      }
      return `rgb(var(--v-theme-${this.activeColor}))`;
    },
  },
  watch: {
    modelValue() {
      this.$nextTick(() => this.updateIndicator());
    },
    options() {
      this.$nextTick(() => this.updateIndicator());
    },
  },
  mounted() {
    const content = this.$el?.querySelector('.v-slide-group__content') as HTMLElement | null;
    const wrapper = content?.parentElement ?? null;
    if (content) {
      this.ensureIndicator(content);
      this.updateIndicator();
      this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
      this.resizeObserver.observe(content);
    }
    if (wrapper) {
      this.scrollParent = wrapper;
      wrapper.addEventListener('scroll', this.updateIndicator);
    }
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
    this.scrollParent?.removeEventListener('scroll', this.updateIndicator);
    this.indicatorEl?.remove();
  },
  methods: {
    ensureIndicator(content: HTMLElement): HTMLElement | null {
      if (this.indicatorEl && content.contains(this.indicatorEl)) {
        return this.indicatorEl;
      }
      const indicator = document.createElement('div');
      indicator.className = 'tab-toggle__indicator';
      content.prepend(indicator);
      this.indicatorEl = indicator;
      return indicator;
    },
    updateIndicator() {
      const content = this.$el?.querySelector('.v-slide-group__content') as HTMLElement | null;
      if (!content) return;

      const el = this.ensureIndicator(content);
      if (!el) return;

      const buttons = content.querySelectorAll('.tab-toggle__btn');
      const activeIndex = this.options.findIndex((opt) => opt.value === this.modelValue);
      if (activeIndex < 0 || activeIndex >= buttons.length) return;

      const btn = buttons[activeIndex] as HTMLElement;
      const contentRect = content.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();

      const left = btnRect.left - contentRect.left;
      const width = btnRect.width;
      const height = btnRect.height;

      const THRESHOLD = 2;
      if (
        Math.abs(left - this.lastLeft) < THRESHOLD &&
        Math.abs(width - this.lastWidth) < THRESHOLD &&
        Math.abs(height - this.lastHeight) < THRESHOLD
      ) {
        return;
      }

      this.lastLeft = left;
      this.lastWidth = width;
      this.lastHeight = height;

      el.style.transform = `translateX(${left}px)`;
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
      el.style.opacity = '1';
    },
  },
});
</script>

<style scoped lang="scss">
.tab-toggle {
  display: inline-flex;
  align-items: stretch;
  background-color: #eceef1;
  border-radius: 8px;
  padding: 3px;

  &--scrollable {
    display: flex;
    min-width: 0;
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  :deep(.v-slide-group__content) {
    position: relative;
    gap: 2px;
  }

  :deep(.tab-toggle__indicator) {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 6px;
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, width 0.2s ease, height 0.2s ease;
    pointer-events: none;
    opacity: 0;
  }

  :deep(.v-slide-group__prev),
  :deep(.v-slide-group__next) {
    flex: 0 0 auto;
    min-width: 28px;
    color: #5f6368;

    &.v-slide-group__prev--disabled,
    &.v-slide-group__next--disabled {
      opacity: 0.3;
    }
  }
}

.tab-toggle__btn {
  position: relative;
  z-index: 1;
  border: none;
  border-radius: 6px;
  background: transparent;
  padding: 6px 16px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: uppercase;
  color: #5f6368;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &:hover:not(.tab-toggle__btn--active) {
    color: #3c4043;
  }

  &--icon-only {
    padding: 6px 10px;
  }

  &--active {
    color: var(--tab-toggle-active-color);
  }

  &--wrap {
    white-space: normal;
    text-align: center;
    max-width: 200px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
</style>
